import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SlowMotionVideoIcon from "@mui/icons-material/SlowMotionVideo";

import { EpisodeWord } from "../../../Episode/services/episode.service";
import EdgeControl from "./parts/EdgeControl";
import FocusTimeline from "./parts/FocusTimeline";
import TranscriptSelector from "./parts/TranscriptSelector";
import {
  ClipEdge,
  MIN_CLIP_LENGTH,
  TimeRange,
  clamp,
  findWordIndexAt,
  fitViewToClip,
  formatTimecode,
  stepWordBoundary,
} from "./utils";

interface VideoTrimmerProps {
  videoUrl: string;
  initialStart: number;
  initialEnd: number;
  onTimeChange: (start: number, end: number) => void;
  maxDuration?: number;
  words?: EpisodeWord[];
  isWordsLoading?: boolean;
}

const EMPTY_WORDS: EpisodeWord[] = [];
const PANEL_HEIGHT = 420;

const SHORTCUTS: [string, string][] = [
  ["Space", "play / pause"],
  ["P", "preview clip"],
  ["I / O", "set start / end"],
  ["← →", "±1s"],
  [", .", "±0.1s"],
  ["F", "fit timeline"],
];

export default function VideoTrimmer({
  videoUrl,
  initialStart,
  initialEnd,
  onTimeChange,
  maxDuration = 1800, // 30 minutes
  words = EMPTY_WORDS,
  isWordsLoading = false,
}: VideoTrimmerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialStart);
  const [videoDuration, setVideoDuration] = useState(0);
  const [trim, setTrim] = useState<TimeRange>([initialStart, initialEnd]);
  const [view, setView] = useState<TimeRange>([0, 0]);
  const [snapToWords, setSnapToWords] = useState(true);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  const trimRef = useRef(trim);
  trimRef.current = trim;
  const isPreviewingRef = useRef(false);

  useEffect(() => {
    setTrim([initialStart, initialEnd]);
    setCurrentTime(initialStart);
    const duration = videoRef.current?.duration;
    if (duration) setView(fitViewToClip([initialStart, initialEnd], duration));
  }, [initialStart, initialEnd]);

  useEffect(() => {
    setIsVideoLoading(true);
  }, [videoUrl]);

  const updateTrim = useCallback(
    (start: number, end: number) => {
      const next: TimeRange = [start, end];
      setTrim(next);
      onTimeChange(start, end);
    },
    [onTimeChange],
  );

  const setEdge = (edge: ClipEdge, time: number) => {
    const [start, end] = trimRef.current;
    if (edge === "start") {
      updateTrim(clamp(time, 0, end - MIN_CLIP_LENGTH), end);
    } else {
      updateTrim(start, clamp(time, start + MIN_CLIP_LENGTH, videoDuration));
    }
  };

  // Setting an edge past the other one moves the whole clip, keeping its length.
  const setEdgeToPlayhead = (edge: ClipEdge) => {
    const [start, end] = trimRef.current;
    const t = videoRef.current?.currentTime ?? currentTime;
    const length = end - start;
    if (edge === "start") {
      if (t > end - MIN_CLIP_LENGTH) {
        updateTrim(t, Math.min(videoDuration, t + length));
      } else {
        updateTrim(t, end);
      }
    } else if (t < start + MIN_CLIP_LENGTH) {
      updateTrim(Math.max(0, t - length), t);
    } else {
      updateTrim(start, t);
    }
  };

  const stepEdgeToWord = (edge: ClipEdge, direction: -1 | 1) => {
    const [start, end] = trimRef.current;
    const next = stepWordBoundary(
      words,
      edge === "start" ? start : end,
      edge,
      direction,
    );
    if (next !== null) setEdge(edge, next);
  };

  const seek = useCallback(
    (time: number) => {
      const video = videoRef.current;
      if (!video) return;
      const t = clamp(time, 0, videoDuration || video.duration || 0);
      video.currentTime = t;
      setCurrentTime(t);
    },
    [videoDuration],
  );

  const stopPreview = () => {
    isPreviewingRef.current = false;
    setIsPreviewing(false);
  };

  const togglePlay = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (!video.paused) {
      video.pause();
      return;
    }
    stopPreview();
    try {
      await video.play();
    } catch (error) {
      console.log("Play was interrupted:", error);
    }
  };

  const previewClip = async () => {
    const video = videoRef.current;
    if (!video) return;
    isPreviewingRef.current = true;
    setIsPreviewing(true);
    seek(trimRef.current[0]);
    try {
      await video.play();
    } catch (error) {
      console.log("Play was interrupted:", error);
      stopPreview();
    }
  };

  // timeupdate only fires ~4x/s; poll per frame so the playhead moves smoothly
  // and a clip preview stops exactly at the end mark.
  useEffect(() => {
    if (!isPlaying) return undefined;
    let frame = 0;
    const tick = () => {
      const video = videoRef.current;
      if (video) {
        if (
          isPreviewingRef.current &&
          video.currentTime >= trimRef.current[1]
        ) {
          video.pause();
          // eslint-disable-next-line prefer-destructuring
          video.currentTime = trimRef.current[1];
          stopPreview();
        }
        setCurrentTime(video.currentTime);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying || !videoDuration) return;
    const [start, end] = view;
    if (currentTime < start || currentTime > end) {
      const span = end - start;
      const nextStart = clamp(
        currentTime - span * 0.15,
        0,
        Math.max(0, videoDuration - span),
      );
      setView([nextStart, nextStart + span]);
    }
  }, [currentTime, isPlaying, view, videoDuration]);

  const shortcutHandlers = useRef({
    togglePlay,
    previewClip,
    setEdgeToPlayhead,
    seek,
    fit: () => {},
  });
  shortcutHandlers.current = {
    togglePlay,
    previewClip,
    setEdgeToPlayhead,
    seek,
    fit: () => setView(fitViewToClip(trimRef.current, videoDuration)),
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        target?.closest("input, textarea, select, [contenteditable='true']")
      ) {
        return;
      }
      const handlers = shortcutHandlers.current;
      const time = videoRef.current?.currentTime ?? 0;
      const actions: Record<string, () => void> = {
        " ": () => void handlers.togglePlay(),
        p: () => void handlers.previewClip(),
        i: () => handlers.setEdgeToPlayhead("start"),
        o: () => handlers.setEdgeToPlayhead("end"),
        ArrowLeft: () => handlers.seek(time - (e.shiftKey ? 5 : 1)),
        ArrowRight: () => handlers.seek(time + (e.shiftKey ? 5 : 1)),
        ",": () => handlers.seek(time - 0.1),
        ".": () => handlers.seek(time + 0.1),
        f: () => handlers.fit(),
      };
      const action = actions[e.key.length === 1 ? e.key.toLowerCase() : e.key];
      if (action) {
        e.preventDefault();
        action();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSelectRange = useCallback(
    (start: number, end: number) => {
      updateTrim(start, Math.max(end, start + MIN_CLIP_LENGTH));
      seek(start);
    },
    [updateTrim, seek],
  );

  const clipLength = trim[1] - trim[0];
  const isOverLimit = clipLength > maxDuration;
  const rawActiveIndex = findWordIndexAt(words, currentTime);
  const activeIndex =
    rawActiveIndex >= 0 && currentTime <= words[rawActiveIndex].end + 0.5
      ? rawActiveIndex
      : -1;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography
          variant="h6"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <ContentCutIcon />
          Clip editor
        </Typography>
        <Chip
          color={isOverLimit ? "error" : "primary"}
          variant={isOverLimit ? "filled" : "outlined"}
          label={`${formatTimecode(clipLength)} / max ${formatTimecode(
            maxDuration,
            false,
          )}`}
          sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 7fr) minmax(0, 5fr)",
          },
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              bgcolor: "black",
              borderRadius: 1.5,
              overflow: "hidden",
              position: "relative",
              aspectRatio: "16 / 9",
            }}
          >
            <video
              ref={videoRef}
              src={videoUrl}
              onLoadStart={() => setIsVideoLoading(true)}
              onCanPlay={() => setIsVideoLoading(false)}
              onLoadedMetadata={() => {
                const video = videoRef.current;
                if (!video) return;
                setVideoDuration(video.duration);
                video.currentTime = initialStart;
                setView(fitViewToClip(trimRef.current, video.duration));
                setIsVideoLoading(false);
              }}
              onTimeUpdate={() => {
                if (!isPlaying && videoRef.current) {
                  setCurrentTime(videoRef.current.currentTime);
                }
              }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => {
                setIsPlaying(false);
                stopPreview();
              }}
              onClick={() => void togglePlay()}
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "contain",
                cursor: "pointer",
              }}
            >
              <track kind="captions" />
              Your browser does not support the video tag.
            </video>

            {isVideoLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  gap: 2,
                }}
              >
                <CircularProgress size={40} sx={{ color: "white" }} />
                <Typography variant="body2">Loading video...</Typography>
              </Box>
            )}
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => void togglePlay()}
              startIcon={
                isPlaying && !isPreviewing ? <PauseIcon /> : <PlayArrowIcon />
              }
            >
              {isPlaying && !isPreviewing ? "Pause" : "Play"}
            </Button>
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() =>
                isPreviewing ? videoRef.current?.pause() : void previewClip()
              }
              startIcon={isPreviewing ? <PauseIcon /> : <SlowMotionVideoIcon />}
            >
              {isPreviewing ? "Stop preview" : "Preview clip"}
            </Button>
            <Typography
              variant="body2"
              sx={{ fontVariantNumeric: "tabular-nums", ml: "auto !important" }}
            >
              {formatTimecode(currentTime)} /{" "}
              {formatTimecode(videoDuration, false)}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
              mt: 1.5,
            }}
          >
            <EdgeControl
              label="Start"
              shortcut="I"
              value={trim[0]}
              onChange={(t) => setEdge("start", t)}
              onSetToPlayhead={() => setEdgeToPlayhead("start")}
              onJump={() => seek(trim[0])}
              onStepWord={
                words.length ? (d) => stepEdgeToWord("start", d) : undefined
              }
            />
            <EdgeControl
              label="End"
              shortcut="O"
              value={trim[1]}
              onChange={(t) => setEdge("end", t)}
              onSetToPlayhead={() => setEdgeToPlayhead("end")}
              onJump={() => seek(Math.max(trim[0], trim[1] - 3))}
              onStepWord={
                words.length ? (d) => stepEdgeToWord("end", d) : undefined
              }
            />
          </Box>
        </Box>

        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            border: 1,
            borderColor: "grey.200",
            borderRadius: 1.5,
            height: { xs: 320, md: PANEL_HEIGHT },
            alignSelf: "start",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderBottom: 1,
              borderColor: "grey.200",
              bgcolor: "grey.50",
            }}
          >
            <Typography variant="subtitle2">Transcript</Typography>
            <Typography variant="caption" color="text.secondary">
              Highlight text to set the clip · click a word to jump there
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <TranscriptSelector
              words={words}
              isLoading={isWordsLoading}
              trim={trim}
              activeIndex={activeIndex}
              followPlayback={isPlaying}
              onSelectRange={handleSelectRange}
              onSeek={seek}
            />
          </Box>
        </Box>
      </Box>

      <FocusTimeline
        duration={videoDuration}
        view={view}
        onViewChange={setView}
        trim={trim}
        onTrimChange={updateTrim}
        currentTime={currentTime}
        onSeek={seek}
        words={words}
        snapToWords={snapToWords}
        onSnapToWordsChange={setSnapToWords}
        isOverLimit={isOverLimit}
      />

      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 0.5 }}
      >
        {SHORTCUTS.map(([keys, description]) => (
          <Typography key={keys} variant="caption" color="text.secondary">
            <Box
              component="kbd"
              sx={{
                mr: 0.5,
                px: 0.5,
                border: 1,
                borderColor: "grey.300",
                borderRadius: 0.5,
                fontFamily: "inherit",
                fontSize: 11,
              }}
            >
              {keys}
            </Box>
            {description}
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
}
